// =============================================================================
// PUBLIC EVENTS — the shared, cross-user external-timeline reference (lake tier).
//
// One row per real-world event (a tournament, conference, programme), researched
// ONCE and reused by everyone. Deliberately NO userId: "FIFA World Cup 2026 ran
// 11 Jun to 19 Jul 2026 across CAN+MEX+USA" is one truth for every user. This is
// the sibling of the planned industry-vocab matrix under the shared-lake roof;
// the precedent is salaryIntel (the existing no-userId reference table).
//
// It exists to flip the PresenceEngine rule from "EVENT FACTS ARE NOT YOURS TO
// RECALL" (prohibition) into "VERIFIED EVENT FACTS you MAY cite" (provided,
// sourced data) — the model still never invents; it now has facts to weave in.
//
// Dates are 'YYYY-MM-DD' parsed downstream by the pure temporal core
// (parseFlexibleDate). lastVerifiedAt feeds the same assessRecency banding every
// other temporal signal uses, so the reference itself stays honest.
// =============================================================================

import { sql } from 'drizzle-orm';
import { date, index, integer, jsonb, pgTable, timestamp, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/** A host of an event: a country, optionally with its host cities. */
export interface EventHost {
  country: string; // 'United States', 'Canada', 'Mexico'
  countryCode?: string; // ISO 3166-1 alpha-2: 'US', 'CA', 'MX'
  cities?: string[];
}

/** Provenance for the researched facts, so a stale or wrong fact is auditable and
 *  re-verifiable, never an anonymous assertion (the no-fabrication rule). */
export interface EventSource {
  url: string;
  title?: string;
  retrievedAt: string; // ISO, STAMPED at research time
}

export const publicEvents = pgTable(
  'public_events',
  {
    id: id(),
    // The dedup key. Slug of the canonical name, e.g. 'fifa-world-cup-2026'.
    // UNIQUE: research-once is enforced here.
    canonicalKey: varchar('canonical_key', { length: 160 }).notNull().unique(),
    // Display name exactly as it should be cited.
    name: varchar('name', { length: 240 }).notNull(),
    // Coarse bucket for filtering: 'tournament' | 'conference' | 'programme' | ...
    kind: varchar('kind', { length: 32 }).notNull().default('event'),
    // Calendar dates (UTC-anchored 'YYYY-MM-DD'); endDate nullable for single-day.
    startDate: date('start_date'),
    endDate: date('end_date'),
    // Host countries + cities (one row can hold a multi-host event).
    hosts: jsonb('hosts').$type<EventHost[]>().default(sql`'[]'::jsonb`),
    // Structured facts that vary by event kind, e.g. { teams: 48, cities: 16 }.
    facts: jsonb('facts').$type<Record<string, unknown>>().default(sql`'{}'::jsonb`),
    // Aliases / abbreviations a user may type: ['World Cup 2026', 'WC26'].
    aliases: jsonb('aliases').$type<string[]>().default(sql`'[]'::jsonb`),
    // Provenance: every fact above traces to these. Empty = unverified seed.
    sources: jsonb('sources').$type<EventSource[]>().default(sql`'[]'::jsonb`),
    // Lifecycle: 'seed' (hand-entered, trusted) | 'researched' (agent + sources) |
    // 'unverified' (looked up, no source) | 'stale' (needs refresh).
    verifyState: varchar('verify_state', { length: 16 }).notNull().default('seed'),
    // When the facts were last confirmed; drives the assessRecency staleness check.
    lastVerifiedAt: timestamp('last_verified_at', { withTimezone: true }),
    // Which model/agent researched it, for audit (mirrors distilled_profiles).
    researchedBy: varchar('researched_by', { length: 64 }),
    confidence: integer('confidence'), // 0-100; seed = 100
    ...timestamps,
  },
  (t) => ({
    keyIdx: index('public_events_key_idx').on(t.canonicalKey),
    kindIdx: index('public_events_kind_idx').on(t.kind),
    startIdx: index('public_events_start_idx').on(t.startDate),
  }),
);
